import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./assets/css/index.css";
import axios from "axios";
import Cookies from "js-cookie";

const API_URL = import.meta.env.VITE_URL_API;

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const publicAxios = axios.create();

function onRefreshed(newToken: string) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = Cookies.get("fin_refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token");
  }

  const res = await publicAxios.post(
    `${API_URL}/user/refresh`,
    JSON.stringify({ refresh_token: refreshToken }),
    { headers: { "Content-Type": "application/json" } }
  );

  const dataResponse = res?.data?.response_data as any;
  if (!dataResponse?.token) {
    throw new Error("Refresh failed");
  }

  const accessToken = dataResponse.token as string;
  const expires = (dataResponse.expires_in || 30 * 60) * 1000;
  const refreshTokenNew = dataResponse.refresh_token as string | undefined;
  const refreshExpires = (dataResponse.refresh_expires_in || 7 * 24 * 60 * 60) * 1000;

  Cookies.set("fin_access_token", accessToken, { expires: new Date(Date.now() + expires) });
  if (refreshTokenNew) {
    Cookies.set("fin_refresh_token", refreshTokenNew, { expires: new Date(Date.now() + refreshExpires) });
  }

  return accessToken;
}

axios.interceptors.request.use((config) => {
  const token = Cookies.get("fin_access_token");
  if (token) {
    config.headers = (config.headers || {}) as any;
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  async (err: any) => {
    const original = err.config as any;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(axios(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        onRefreshed(newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      } catch (e) {
        isRefreshing = false;
        refreshSubscribers = [];
        Cookies.remove("fin_access_token");
        Cookies.remove("fin_refresh_token");
        window.location.href = "/login";
        return Promise.reject(e);
      }
    }
    return Promise.reject(err);
  }
);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
